/**
 * ImportReviewWizard
 *
 * Top-level orchestrator for the multi-step import review experience.
 *   Preview → New Patients → Matched Patients → Appointments
 * Steps with zero data are skipped automatically.
 */

import { useCallback } from 'react';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import { AnimatePresence, motion } from 'motion/react';

import { AmbiguousPatientsBanner } from './components/AmbiguousPatientsBanner';
import { WizardNavigation } from './components/WizardNavigation';
import { AnimatedPreview } from './steps/AnimatedPreview';
import { AppointmentsStep } from './steps/AppointmentsStep';
import { MatchedPatientsStep } from './steps/MatchedPatientsStep';
import { NewPatientsStep } from './steps/NewPatientsStep';
import type { ImportReviewWizardProps } from './ImportReviewWizard.types';
import { useImportReviewWizard } from './useImportReviewWizard';

// =============================================================================
// Animation Variants
// =============================================================================

const slideVariants = {
  enterFromRight: { x: 60, opacity: 0 },
  enterFromLeft: { x: -60, opacity: 0 },
  center: { x: 0, opacity: 1 },
  exitToLeft: { x: -60, opacity: 0 },
  exitToRight: { x: 60, opacity: 0 },
};

const slideTransition = {
  type: 'tween' as const,
  ease: 'easeOut' as const,
  duration: 0.25,
};

// =============================================================================
// Component
// =============================================================================

export const ImportReviewWizard = ({ sessionId, onCancel }: ImportReviewWizardProps) => {
  const wizard = useImportReviewWizard({ sessionId });

  // ---------------------------------------------------------------------------
  // Preview continue handler
  // ---------------------------------------------------------------------------

  const handlePreviewContinue = useCallback(() => {
    wizard.markPreviewComplete();
    if (!wizard.isLastStep) {
      wizard.goNext();
    }
  }, [wizard]);

  // ---------------------------------------------------------------------------
  // Navigation handlers
  // ---------------------------------------------------------------------------

  const handleNext = useCallback(() => {
    if (wizard.isLastStep) {
      wizard.handleApprove();
    } else {
      wizard.goNext();
    }
  }, [wizard]);

  const handleSkipAppointments = useCallback(() => {
    wizard.handleApprove({ skipAppointments: true });
  }, [wizard]);

  // ---------------------------------------------------------------------------
  // Loading State
  // ---------------------------------------------------------------------------

  if (wizard.isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 10,
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          Preparing your review...
        </Typography>
      </Box>
    );
  }

  // ---------------------------------------------------------------------------
  // Error State
  // ---------------------------------------------------------------------------

  if (wizard.error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Failed to load review summary. Please try again.
      </Alert>
    );
  }

  // ---------------------------------------------------------------------------
  // Empty State
  // ---------------------------------------------------------------------------

  if (wizard.totalRecords === 0) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        No patients found in this import session.
      </Alert>
    );
  }

  // ---------------------------------------------------------------------------
  // Step Content — each step fetches its own phase data lazily
  // ---------------------------------------------------------------------------

  const renderStep = () => {
    switch (wizard.activeStepId) {
      case 'preview':
        return (
          <AnimatedPreview
            counts={wizard.counts}
            onContinue={handlePreviewContinue}
            onCancel={onCancel}
          />
        );
      case 'new-patients':
        return <NewPatientsStep sessionId={sessionId} />;
      case 'matched-patients':
        return <MatchedPatientsStep sessionId={sessionId} />;
      case 'appointments':
        return <AppointmentsStep sessionId={sessionId} />;
      default:
        return null;
    }
  };

  const isPreview = wizard.activeStepId === 'preview';

  return (
    <Box>
      {/* Ambiguous banner — persistent across all non-preview steps */}
      {!isPreview && wizard.counts.ambiguousCount > 0 && (
        <AmbiguousPatientsBanner count={wizard.counts.ambiguousCount} />
      )}

      {/* Step content with animated transitions */}
      <AnimatePresence mode="wait">
        <motion.div
          key={wizard.activeStepIndex}
          initial={wizard.slideDirection === 'left' ? 'enterFromRight' : 'enterFromLeft'}
          animate="center"
          exit={wizard.slideDirection === 'left' ? 'exitToLeft' : 'exitToRight'}
          variants={slideVariants}
          transition={slideTransition}
        >
          {renderStep()}
        </motion.div>
      </AnimatePresence>

      {/* Navigation footer — hidden on preview (it has its own Continue button) */}
      {!isPreview && (
        <WizardNavigation
          onBack={wizard.goBack}
          onNext={handleNext}
          onSkipAppointments={handleSkipAppointments}
          isFirstStep={wizard.activeStepIndex <= 1}
          isLastStep={wizard.isLastStep}
          isApproving={wizard.isApproving}
          showSkipAppointments={wizard.activeStepId === 'appointments'}
        />
      )}
    </Box>
  );
};
